import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../api/axios';
import AdminLayout from '../../layouts/AdminLayout';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import ImageUploader from '../../components/ImageUploader';

const ProductEdit = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [unitType, setUnitType] = useState('piece');
    const [minOrder, setMinOrder] = useState(1);
    const [maxOrder, setMaxOrder] = useState('');
    const [transportFee, setTransportFee] = useState(0);
    const [baseSku, setBaseSku] = useState(''); // Odoo product.template.default_code

    // Ana görseller
    const [mainImageItems, setMainImageItems] = useState([]);
    const [deletedMainImageIds, setDeletedMainImageIds] = useState([]);

    // Kategoriler
    const [categories, setCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);

    // Varyasyonlar
    const [variations, setVariations] = useState([]);
    const [deletedVarImageIds, setDeletedVarImageIds] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [catRes, prodRes] = await Promise.all([
                    axios.get('/categories'),
                    axios.get(`/products/${id}`)
                ]);

                setCategories(catRes.data);
                const product = prodRes.data;
                setName(product.name);
                setDescription(product.description || '');
                setUnitType(product.unit_type);
                setMinOrder(product.min_order_limit);
                setMaxOrder(product.max_order_limit || '');
                setTransportFee(product.transport_fee);
                setBaseSku(product.base_sku || '');

                if (product.categories) {
                    setSelectedCategories(product.categories.map(c => c.id));
                }

                // Ana görseller
                const sortedImages = (product.images || [])
                    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                    .map(img => ({
                        type: 'existing', id: img.id,
                        image_url: img.image_url,
                        tempId: `ex_${img.id}`
                    }));
                setMainImageItems(sortedImages);

                // Varyasyonlar
                const vars = product.variations && product.variations.length > 0
                    ? product.variations : [{ size: '', color: '', sku: '', dealer_price: '', retail_price: '', stock_quantity: 0, use_main_image: false, imageItems: [] }];

                const varsWithItems = vars.map(v => ({
                    ...v,
                    imageItems: (v.images || [])
                        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                        .map(img => ({
                            type: 'existing', id: img.id,
                            image_url: img.image_url,
                            tempId: `ex_${img.id}`
                        }))
                }));
                setVariations(varsWithItems);

            } catch (error) {
                console.error('Veriler yüklenemedi', error);
                toast.error(t('messages.product_info_error'));
            }
        };
        fetchData();
    }, [id, t]);

    const addCategory = (categoryId) => {
        const catId = Number(categoryId);
        if (catId && !selectedCategories.includes(catId)) {
            setSelectedCategories([...selectedCategories, catId]);
        }
    };

    const removeCategory = (categoryId) => {
        setSelectedCategories(selectedCategories.filter(i => i !== categoryId));
    };

    const handleVariationChange = (index, field, value) => {
        const newVariations = [...variations];
        newVariations[index][field] = value;
        setVariations(newVariations);
    };

    const addVariationRow = () => {
        setVariations([...variations, { size: '', color: '', sku: '', dealer_price: '', retail_price: '', stock_quantity: 0, use_main_image: false, imageItems: [] }]);
    };

    const removeVariationRow = (index) => {
        setVariations(variations.filter((_, i) => i !== index));
    };

    // Ana görsel değiştiğinde silinen existing öğeleri takip et
    const handleMainImagesChange = (newItems) => {
        const prevExisting = mainImageItems.filter(i => i.type === 'existing');
        const newExisting = newItems.filter(i => i.type === 'existing');
        const deleted = prevExisting.filter(old => !newExisting.find(n => n.id === old.id)).map(i => i.id);
        if (deleted.length > 0) {
            setDeletedMainImageIds(prev => [...new Set([...prev, ...deleted])]);
        }
        setMainImageItems(newItems);
    };

    // Varyasyon görseli değiştiğinde silinen existing öğeleri takip et
    const handleVariationImagesChange = (index, newItems) => {
        const newVariations = [...variations];
        const prevExisting = (newVariations[index].imageItems || []).filter(i => i.type === 'existing');
        const newExisting = newItems.filter(i => i.type === 'existing');
        const deleted = prevExisting.filter(old => !newExisting.find(n => n.id === old.id)).map(i => i.id);
        if (deleted.length > 0) {
            setDeletedVarImageIds(prev => [...new Set([...prev, ...deleted])]);
        }
        newVariations[index].imageItems = newItems;
        setVariations(newVariations);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const formData = new FormData();
            formData.append('name', name);
            formData.append('description', description);
            formData.append('unit_type', unitType);
            formData.append('min_order_limit', minOrder);
            if (maxOrder) formData.append('max_order_limit', maxOrder);
            formData.append('transport_fee', transportFee);
            if (baseSku.trim()) formData.append('base_sku', baseSku.trim());
            formData.append('categoryIds', JSON.stringify(selectedCategories));

            // Silinen ana görseller
            formData.append('deletedMainImageIds', JSON.stringify(deletedMainImageIds));

            // Ana görsellerin mevcut sırası (existing)
            const mainImageOrder = mainImageItems
                .filter(i => i.type === 'existing')
                .map((item, idx) => ({ id: item.id, sort_order: idx }));
            formData.append('mainImageOrder', JSON.stringify(mainImageOrder));

            // Yeni ana görseller
            mainImageItems.filter(i => i.type === 'new').forEach(item => {
                formData.append('images', item.file);
            });

            // Varyasyonlar (imageItems field'ı serialize edilemez, temizle)
            const variationsToSend = variations.map(({ imageItems, ...rest }) => ({
                ...rest,
                // Mevcut varyasyon görsellerinin yeni sırası
                images: (imageItems || [])
                    .filter(i => i.type === 'existing')
                    .map((item, idx) => ({ id: item.id, sort_order: idx }))
            }));
            formData.append('variations', JSON.stringify(variationsToSend));

            // Silinen varyasyon görselleri
            formData.append('deletedVarImageIds', JSON.stringify(deletedVarImageIds));

            // Yeni varyasyon görselleri
            variations.forEach((variant, vi) => {
                if (!variant.use_main_image && variant.imageItems) {
                    variant.imageItems.filter(i => i.type === 'new').forEach((item, ji) => {
                        formData.append(`var_img_${vi}_${ji}`, item.file);
                    });
                }
            });

            await axios.put(`/products/${id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success(t('messages.product_updated'));
            navigate('/admin/products');
        } catch (error) {
            toast.error(error.response?.data?.message || t('messages.product_update_error'));
        }
    };

    return (
        <AdminLayout>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>{t('product_form.edit_title')}</h2>
                <button onClick={() => navigate('/admin/products')} className="btn btn-secondary">{t('product_form.back')}</button>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="row">
                    <div className="col-md-8">
                        <div className="card mb-4 shadow-sm">
                            <div className="card-header bg-light">{t('product_form.general_info')}</div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <label className="form-label">{t('product_form.product_name')}</label>
                                    <input type="text" className="form-control" value={name} onChange={e => setName(e.target.value)} required />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label" style={{color: '#7c3aed', fontWeight: 600}}>
                                        🔗 Odoo Stok Kodu (Base SKU)
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="base_sku_edit"
                                        placeholder="Örn: VM-HALI-001 (isteğe bağlı)"
                                        value={baseSku}
                                        onChange={e => setBaseSku(e.target.value)}
                                    />
                                    <small className="text-muted">
                                        Odoo'daki <code>product.template.default_code</code> değeriyle eşleşmelidir.
                                    </small>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">{t('product_form.description')}</label>
                                    <ReactQuill theme="snow" value={description} onChange={setDescription} style={{ height: '200px', marginBottom: '50px' }} />
                                </div>
                                <div className="mb-3">
                                    <ImageUploader
                                        items={mainImageItems}
                                        onChange={handleMainImagesChange}
                                        label={t('product_form.images')}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Varyasyonlar */}
                        <div className="card mb-4 shadow-sm">
                            <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                <span>{t('product_form.variations_title')}</span>
                                <button type="button" className="btn btn-sm btn-primary" onClick={addVariationRow}>{t('product_form.add_variation')}</button>
                            </div>
                            <div className="card-body">
                                {variations.map((variant, index) => (
                                    <div key={variant.id || index} className="border rounded p-3 mb-3">
                                        <div className="row g-2 mb-2 align-items-end">
                                            <div className="col-md-2 col-6">
                                                <label className="form-label small">{t('product_form.size')}</label>
                                                <input type="text" className="form-control form-control-sm" placeholder="100x200"
                                                    value={variant.size || ''} onChange={e => handleVariationChange(index, 'size', e.target.value)} />
                                            </div>
                                            <div className="col-md-2 col-6">
                                                <label className="form-label small">{t('product_form.color')}</label>
                                                <input type="text" className="form-control form-control-sm" placeholder="Antrasit"
                                                    value={variant.color || ''} onChange={e => handleVariationChange(index, 'color', e.target.value)} />
                                            </div>
                                            <div className="col-md-2 col-6">
                                                <label className="form-label small">{t('product_form.dealer_price')}</label>
                                                <input type="number" className="form-control form-control-sm" placeholder="0.00" required
                                                    value={variant.dealer_price || ''} onChange={e => handleVariationChange(index, 'dealer_price', e.target.value)} />
                                            </div>
                                            <div className="col-md-2 col-6">
                                                <label className="form-label small">{t('product_form.sales_price')}</label>
                                                <input type="number" className="form-control form-control-sm" placeholder="0.00" required
                                                    value={variant.retail_price || ''} onChange={e => handleVariationChange(index, 'retail_price', e.target.value)} />
                                            </div>
                                            <div className="col-md-2 col-6">
                                                <label className="form-label small">{t('product_form.stock')}</label>
                                                <input type="number" className="form-control form-control-sm" placeholder="0"
                                                    value={variant.stock_quantity || 0} onChange={e => handleVariationChange(index, 'stock_quantity', e.target.value)} />
                                            </div>
                                            <div className="col-md-2 text-end">
                                                <button type="button" className="btn btn-sm btn-danger" onClick={() => removeVariationRow(index)}>{t('product_form.delete')}</button>
                                            </div>
                                        </div>
                                        <div className="border-top pt-2 mt-1">
                                            <div className="form-check form-switch mb-2">
                                                <input className="form-check-input" type="checkbox"
                                                    checked={variant.use_main_image || false}
                                                    onChange={e => handleVariationChange(index, 'use_main_image', e.target.checked)}
                                                />
                                                <label className="form-check-label small">{t('product_form.main_image')}</label>
                                            </div>
                                            {!variant.use_main_image && (
                                                <ImageUploader
                                                    items={variant.imageItems || []}
                                                    onChange={(items) => handleVariationImagesChange(index, items)}
                                                    label="Varyasyon Görselleri"
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Sağ Taraf */}
                    <div className="col-md-4">
                        <div className="card mb-4 shadow-sm">
                            <div className="card-header bg-light">{t('product_form.sales_settings')}</div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <label className="form-label">{t('product_form.sales_unit')}</label>
                                    <select className="form-select" value={unitType} onChange={e => setUnitType(e.target.value)}>
                                        <option value="piece">{t('common.unit_piece')}</option>
                                        <option value="m2">{t('common.unit_m2')}</option>
                                        <option value="pallet">{t('common.unit_pallet')}</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">{t('product_form.min_order')} ({unitType})</label>
                                    <input type="number" className="form-control" value={minOrder} onChange={e => setMinOrder(e.target.value)} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">{t('product_form.shipping_fee')}</label>
                                    <input type="number" className="form-control" value={transportFee} onChange={e => setTransportFee(e.target.value)} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">{t('product_form.categories')}</label>
                                    <div className="input-group mb-2">
                                        <select className="form-select" onChange={(e) => addCategory(e.target.value)} value="">
                                            <option value="">{t('product_form.select_category')}</option>
                                            {categories.filter(cat => !selectedCategories.includes(cat.id)).map(cat => (
                                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                                            ))}
                                        </select>
                                        <button className="btn btn-outline-secondary" type="button" disabled>+</button>
                                    </div>
                                    <div className="d-flex flex-wrap gap-2">
                                        {selectedCategories.map(catId => {
                                            const cat = categories.find(c => c.id === catId);
                                            return cat ? (
                                                <span key={cat.id} className="badge bg-primary d-flex align-items-center p-2">
                                                    {cat.name}
                                                    <button type="button" className="btn-close btn-close-white ms-2" style={{ fontSize: '0.5rem' }} onClick={() => removeCategory(cat.id)}></button>
                                                </span>
                                            ) : null;
                                        })}
                                    </div>
                                    {selectedCategories.length === 0 && <small className="text-muted">{t('product_form.no_category_selected')}</small>}
                                </div>
                                <button type="submit" className="btn btn-primary w-100">{t('product_form.save_changes')}</button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </AdminLayout>
    );
};

export default ProductEdit;
